/**
 * Block 14 «Раскрой» — АДАПТИВНЫЕ подписи деталей на карте раскроя (CutMap).
 *
 * Разворачивает прежнее решение «единый шрифт» (#155) обратно к пропорциональному.
 * Шрифт привязан к РАЗМЕРУ ДЕТАЛИ (короткой стороне), а не к листу — большой лист
 * с мелкими деталями больше не даёт гигантский единый шрифт; верхний кап `FONT_MAX`
 * страхует обратное («не гигантский на больших листах»). Геометрия (inset кромок,
 * повороты, веса) тюнится визуально на превью в Chrome.
 *
 * Подпись куска: размер + номер инлайн, номер — последний элемент через запятую:
 *  • крупная с именем → центр «Имя, №» + размеры вдоль КРОМОК (длина/ширина);
 *  • крупная без имени → центр ТОЛЬКО «№» + размеры вдоль кромок (без дубля «Д × Ш»);
 *  • узкая тонкая / мелкая → «Д × Ш, №» (одна строка);
 *  • узкая толстая именованная → две строки: «Имя» и «Д × Ш, №»;
 *  • остаток крупный → размеры вдоль КРОМОК + центр «Остаток, О№»;
 *  • остаток узкий/мелкий → «Д × Ш, О№» (одна строка).
 * Номера совпадают с таблицами «Список деталей»/«Список остатков» (общий источник).
 *
 * Раскладка (в user-units SVG = мм, viewBox = габарит листа): КРУПНАЯ — edge-числа +
 * центральная подпись; УЗКАЯ/МЕЛКАЯ — центральная подпись, повёрнута вдоль длинной оси
 * при h>w. Остатки подписываются ПО АНАЛОГИИ с деталями (решение владельца): крупный
 * остаток дробится на edge-числа + «Остаток» по центру; узкий/мелкий — инлайн «Д × Ш, О№».
 */

/** shortSide < этого → МЕЛКАЯ (мм). */
export const SMALL_SHORT = 120;
/** longSide < этого → МЕЛКАЯ (крошечный квадратик) (мм). */
export const SMALL_LONG = 300;
/** longSide / shortSide ≥ этого (и не мелкая) → УЗКАЯ. */
export const NARROW_ASPECT = 4;

/** Базовый шрифт = shortSide * FONT_RATIO. */
export const FONT_RATIO = 0.22;
/** Нижний порог шрифта (мелкая деталь всё ещё читаема) (мм). */
export const FONT_MIN = 22;
/** Верхний кап шрифта — «не гигантский на больших листах» (мм). */
export const FONT_MAX = 160;
/**
 * Множитель шрифта ТОЛЬКО для режима 'small' — мелкая деталь использует максимум
 * своего места, чтобы «Д × Ш, №» читалось. Boost страхуется fit-guard'ом (`fitFont`):
 * на вытянутых мелких деталях строка всё равно не вылезет за кромку. Крупные/узкие/
 * остатки множитель не трогает.
 */
export const SMALL_FONT_BOOST = 1.5;
/** Средняя ширина глифа / fontSize — для fit-guard (чтобы строка влезла в кромку). */
const CHAR_W = 0.6;
/** УЗКАЯ деталь: имя показываем, только если короткая сторона ≥ этого (мм). */
export const NAME_MIN_THICK = 180;

export type LabelMode = 'large' | 'narrow' | 'small';

/** Одна <text>-подпись: координаты в локальной системе детали (origin = верх-лево). */
export interface LabelText {
  key: string;
  cls: string;
  content: string;
  x: number;
  y: number;
  fontSize: number;
  /** Индивидуальный transform (edge-подписи КРУПНОЙ детали поворачиваются по одной). */
  transform?: string;
}

/** Набор подписей одного куска + общий transform группы (поворот узкой/мелкой/остатка). */
export interface PieceLabels {
  groupTransform?: string;
  texts: LabelText[];
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/** Классификация режима подписи по габаритам куска. Порядок: мелкая → узкая → крупная. */
export function classifyLabelMode(w: number, h: number): LabelMode {
  const short = Math.min(w, h);
  const long = Math.max(w, h);
  if (short < SMALL_SHORT || long < SMALL_LONG) return 'small';
  if (long / short >= NARROW_ASPECT) return 'narrow';
  return 'large';
}

/** Базовый (пропорциональный) шрифт от короткой стороны куска, с нижним/верхним капом. */
export function baseFont(w: number, h: number): number {
  return clamp(Math.min(w, h) * FONT_RATIO, FONT_MIN, FONT_MAX);
}

/**
 * Fit-guard: ужать шрифт так, чтобы строка из `textLen` символов влезла в `track`
 * (длину кромки/оси, вдоль которой идёт текст). 90% трека — поля по краям.
 */
export function fitFont(font: number, textLen: number, track: number): number {
  if (textLen <= 0) return font;
  return Math.min(font, (track * 0.9) / (textLen * CHAR_W));
}

/** Комбинированная подпись «Д × Ш» = длина × ширина (с пробелами, решение владельца). */
function sizeText(long: number, short: number): string {
  return `${long} × ${short}`;
}

/**
 * Центральная подпись = «база, №» (имя/«остаток» + номер). Если номера нет — только
 * база; если базы нет (мелкая деталь без имени) — только номер. Пусто → ''.
 */
export function joinCaption(baseLabel: string, num?: number): string {
  if (num == null) return baseLabel;
  const n = String(num);
  return baseLabel ? `${baseLabel}, ${n}` : n;
}

/**
 * Подписи ДЕТАЛИ (центральная «Имя, №» + размеры) по адаптивному режиму.
 * `w`/`h` — фактические габариты прямоугольника детали (x→w, y→h в локальной СК).
 * `num` — номер детали из «Список деталей» (часть центральной подписи; опционален).
 */
export function buildPartLabels(w: number, h: number, name: string, num?: number): PieceLabels {
  const mode = classifyLabelMode(w, h);
  const base = baseFont(w, h);
  const wr = Math.round(w);
  const hr = Math.round(h);
  const long = Math.max(wr, hr);
  const short = Math.min(wr, hr);
  const cx = w / 2;
  const cy = h / 2;

  if (mode === 'large') {
    // Размеры вдоль кромок: длинное число — вдоль длинной стороны, короткое —
    // вдоль короткой (повёрнуто). Центр — «Имя, №». Без поворота группы — каждая
    // edge-подпись несёт свой transform (избегаем двойного вращения).
    const longTxt = String(long);
    const shortTxt = String(short);
    const longFont = fitFont(base, longTxt.length, long); // текст идёт вдоль длинной стороны
    const shortFont = fitFont(base, shortTxt.length, short); // вдоль короткой
    const inset = base * 0.7; // отступ подписи от кромки
    const texts: LabelText[] = [];

    if (w >= h) {
      // Landscape: длинная сторона — горизонталь (низ), короткая — вертикаль (лево).
      texts.push({
        key: 'long',
        cls: 'part-edge-label part-edge-long',
        content: longTxt,
        x: cx,
        y: h - inset,
        fontSize: longFont,
      });
      texts.push({
        key: 'short',
        cls: 'part-edge-label part-edge-short',
        content: shortTxt,
        x: inset,
        y: cy,
        fontSize: shortFont,
        transform: `rotate(-90 ${inset} ${cy})`,
      });
    } else {
      // Portrait: длинная сторона — вертикаль (лево), короткая — горизонталь (низ).
      texts.push({
        key: 'long',
        cls: 'part-edge-label part-edge-long',
        content: longTxt,
        x: inset,
        y: cy,
        fontSize: longFont,
        transform: `rotate(-90 ${inset} ${cy})`,
      });
      texts.push({
        key: 'short',
        cls: 'part-edge-label part-edge-short',
        content: shortTxt,
        x: cx,
        y: h - inset,
        fontSize: shortFont,
      });
    }

    // Центральная подпись (одна строка): с именем → «Имя, №»; без имени → ТОЛЬКО номер
    // (размеры уже на кромках — «Д × Ш» в центре дублировал бы их). Горизонтальна →
    // влезает по ширине w.
    const caption = name ? joinCaption(name, num) : num != null ? String(num) : '';
    if (caption) {
      const cls = name ? 'part-name-label' : 'part-size-label';
      const capFont = fitFont(name ? base * 0.8 : base, caption.length, w);
      texts.push({ key: 'caption', cls, content: caption, x: cx, y: cy, fontSize: capFont });
    }

    return { texts };
  }

  // УЗКАЯ / МЕЛКАЯ: размер + номер инлайн «Д × Ш, №». Узкая ТОЛСТАЯ именованная —
  // две строки: имя сверху, «Д × Ш, №» снизу (без отдельного num-лейбла). Поворот
  // группы при h>w (текст вдоль длинной оси, читается снизу-вверх).
  const nameForCaption = mode === 'narrow' && short >= NAME_MIN_THICK ? name : '';
  const sizeCaption = joinCaption(sizeText(long, short), num); // «Д × Ш, №»
  // Мелкая деталь — крупнее (boost), всё ещё ограничена fit-guard'ом по длинной оси.
  const captionBase = mode === 'small' ? base * SMALL_FONT_BOOST : base;
  const sizeFont = fitFont(captionBase, sizeCaption.length, long);
  const groupTransform = h > w ? `rotate(-90 ${cx} ${cy})` : undefined;

  if (nameForCaption) {
    const nameFont = fitFont(captionBase, nameForCaption.length, long);
    return {
      groupTransform,
      texts: [
        {
          key: 'name',
          cls: 'part-name-label',
          content: nameForCaption,
          x: cx,
          y: cy - sizeFont * 0.7,
          fontSize: nameFont,
        },
        {
          key: 'size',
          cls: 'part-size-label',
          content: sizeCaption,
          x: cx,
          y: cy + sizeFont * 0.7,
          fontSize: sizeFont,
        },
      ],
    };
  }

  return {
    groupTransform,
    texts: [
      {
        key: 'size',
        cls: 'part-size-label',
        content: sizeCaption,
        x: cx,
        y: cy,
        fontSize: sizeFont,
      },
    ],
  };
}

/** О-номер остатка для подписи: «О3», либо '' если номера нет. */
function offcutNum(num?: number): string {
  return num != null ? `О${num}` : '';
}

/**
 * Подпись годного ОСТАТКА (freeRect) — адаптивная, ПО АНАЛОГИИ с деталью (решение
 * владельца). Режим тот же, что у деталей (`classifyLabelMode`):
 *  • КРУПНЫЙ → размеры вдоль КРОМОК (длина/ширина, короткое число повёрнуто) + центр
 *    «Остаток, О№» (или «Остаток» без номера); edge-подписи крутятся индивидуально,
 *    поворота группы нет;
 *  • УЗКИЙ/МЕЛКИЙ → инлайн «Д × Ш, О№» (без слова «Остаток»), поворот группы при h>w.
 * Все подписи — класс 'free-rect-label'.
 */
export function buildOffcutLabels(w: number, h: number, num?: number): PieceLabels {
  const mode = classifyLabelMode(w, h);
  const base = baseFont(w, h);
  const long = Math.max(Math.round(w), Math.round(h));
  const short = Math.min(Math.round(w), Math.round(h));
  const cx = w / 2;
  const cy = h / 2;
  const oNum = offcutNum(num);

  if (mode === 'large') {
    // Размеры вдоль кромок (как у крупной детали): длинное число — вдоль длинной
    // стороны, короткое — повёрнуто вдоль короткой. Без поворота группы — каждая
    // edge-подпись несёт свой transform.
    const longTxt = String(long);
    const shortTxt = String(short);
    const longFont = fitFont(base, longTxt.length, long);
    const shortFont = fitFont(base, shortTxt.length, short);
    const inset = base * 0.7;
    const texts: LabelText[] = [];

    if (w >= h) {
      texts.push({
        key: 'long',
        cls: 'free-rect-label',
        content: longTxt,
        x: cx,
        y: h - inset,
        fontSize: longFont,
      });
      texts.push({
        key: 'short',
        cls: 'free-rect-label',
        content: shortTxt,
        x: inset,
        y: cy,
        fontSize: shortFont,
        transform: `rotate(-90 ${inset} ${cy})`,
      });
    } else {
      texts.push({
        key: 'long',
        cls: 'free-rect-label',
        content: longTxt,
        x: inset,
        y: cy,
        fontSize: longFont,
        transform: `rotate(-90 ${inset} ${cy})`,
      });
      texts.push({
        key: 'short',
        cls: 'free-rect-label',
        content: shortTxt,
        x: cx,
        y: h - inset,
        fontSize: shortFont,
      });
    }

    // Центр: «Остаток, О№» (или «Остаток» без номера). Шрифт пропорционален (base * 0.8),
    // как у центра крупной детали, но с fit-guard по длинной стороне (long).
    const caption = oNum ? `Остаток, ${oNum}` : 'Остаток';
    // Текст идёт ВДОЛЬ ДЛИННОЙ СТОРОНЫ → fit по long; поворот при портрете (h>w).
    const capFont = fitFont(base * 0.8, caption.length, long);
    texts.push({
      key: 'caption',
      cls: 'free-rect-label',
      content: caption,
      x: cx,
      y: cy,
      fontSize: capFont,
      transform: h > w ? `rotate(-90 ${cx} ${cy})` : undefined,
    });

    return { texts };
  }

  // УЗКИЙ / МЕЛКИЙ: инлайн «Д × Ш, О№» (без слова «Остаток»). Поворот группы при h>w.
  const sizeTxt = sizeText(long, short);
  const caption = oNum ? `${sizeTxt}, ${oNum}` : sizeTxt;
  const captionBase = mode === 'small' ? base * SMALL_FONT_BOOST : base;
  const capFont = fitFont(captionBase, caption.length, long);
  const groupTransform = h > w ? `rotate(-90 ${cx} ${cy})` : undefined;

  return {
    groupTransform,
    texts: [
      { key: 'size', cls: 'free-rect-label', content: caption, x: cx, y: cy, fontSize: capFont },
    ],
  };
}
